import { CreativeDocumentMedium } from 'content-creation-common/models';
import { isNumber } from '../util/numbers';
import {
  deepEqual,
  isArray,
  isBoolean,
  isEmpty as isEmptyObject,
  isNil,
  isNotEmptyString,
  isNotNil,
} from '../util/object';
import {
  getMediaTypeForMimeType,
  getMediaUrlByMedia,
  getThumbnailUrl,
} from '../util/mediaUtils';
import { appendQueryParams } from '../util/queryString';

// TODO (lemieux) extend WireObject
export default class Medium {
  _audioServerId;
  _audioStreamDuration;
  _audioStreamStartTime;
  _centerX;
  _createdAt;
  _createdBy;
  _duration;
  _fileName;
  _fontAdjustment;
  _frameFormat;
  _fromTemplate;
  _height;
  _loaded;
  _localUrl;
  _mediaId;
  _mediaType;
  _medium;
  _mimeType;
  _numFrames;
  _organizationId;
  _processedAudio;
  _processors;
  _publisherId;
  _serverId;
  _source;
  _svg;
  _templateId;
  _updatedAt;
  _uploadComplete;
  _uploaded;
  _uploadedPercent;
  _uploadFailed;
  _URL;
  _usePngSequence;
  _variants;
  _width;

  /** @param {Object} medium
   * @param {String|Undefined} templateId */
  constructor(medium, templateId = undefined) {
    // TODO get rid of this
    this._medium = medium;

    if (!isEmptyObject(medium)) {
      const {
        audioServerId,
        audioStreamDuration,
        audioStreamStartTime,
        centerX,
        createdAt,
        createdBy,
        duration,
        fileName,
        fontAdjustment,
        frameFormat,
        fromTemplate,
        height,
        loaded,
        localUrl,
        mediaId,
        mediaType,
        mimeType,
        numFrames,
        organizationId,
        processedAudio,
        processors,
        publisherId,
        serverId,
        source,
        svg,
        templateId: mediumTemplateId,
        updatedAt,
        uploadComplete,
        uploaded,
        uploadedPercent,
        uploadFailed,
        usePngSequence,
        variants,
        width,
      } = medium;

      if (isNumber(fontAdjustment)) {
        this._fontAdjustment = fontAdjustment;
      }

      if (isNotNil(mediaId)) {
        this._mediaId = mediaId;
      } else {
        this._mediaId = CreativeDocumentMedium.getId();
      }

      if (isNotNil(height)) {
        this._height = height;
      }

      if (isNotNil(width)) {
        this._width = width;
      }

      if (isNotNil(duration)) {
        this._duration = duration;
      }

      if (isNotNil(audioServerId)) {
        this._audioServerId = audioServerId;
      }

      if (isNotNil(audioStreamDuration)) {
        this._audioStreamDuration = audioStreamDuration;
      }

      if (isNotNil(audioStreamStartTime)) {
        this._audioStreamStartTime = audioStreamStartTime;
      }

      if (isNotNil(centerX)) {
        this._centerX = centerX;
      }

      if (isNotNil(mediaType)) {
        this._mediaType = mediaType;
      }

      if (isNotNil(mimeType)) {
        this._mimeType = mimeType;
      }

      if (isBoolean(loaded)) {
        this._loaded = loaded;
      }

      if (isNotNil(organizationId)) {
        this._organizationId = organizationId;
      }

      if (isBoolean(processedAudio)) {
        this._processedAudio = processedAudio;
      }

      if (isArray(processors)) {
        this._processors = processors;
      }

      if (isNotNil(publisherId)) {
        this._publisherId = publisherId;
      }

      if (isNotNil(serverId)) {
        this._serverId = serverId;
      }

      if (isNotNil(source)) {
        this._source = source;
      }

      if (isNotNil(svg)) {
        this._svg = svg;
      }

      if (isNotNil(uploadComplete)) {
        this._uploadComplete = uploadComplete;
      }

      if (isNotNil(uploadedPercent)) {
        this._uploadedPercent = uploadedPercent;
      }

      if (isNotNil(uploadFailed)) {
        this._uploadFailed = uploadFailed;
      }

      if (isNotNil(frameFormat)) {
        this._frameFormat = frameFormat;
      }

      if (isNotNil(fromTemplate)) {
        this._fromTemplate = fromTemplate;
      }

      if (isNotNil(mediumTemplateId)) {
        this._templateId = mediumTemplateId;
      }

      if (isNotNil(variants)) {
        this._variants = variants;
      }

      if (isNotNil(createdAt)) {
        this._createdAt = createdAt;
      }

      if (isNotNil(createdBy)) {
        this._createdBy = createdBy;
      }

      if (isNotNil(fileName)) {
        this._fileName = fileName;
      }

      if (isNotNil(numFrames)) {
        this._numFrames = numFrames;
      }

      if (isNotNil(updatedAt)) {
        this._updatedAt = updatedAt;
      }

      if (isNotNil(usePngSequence)) {
        this._usePngSequence = usePngSequence;
      }

      if (isNotNil(localUrl)) {
        this._localUrl = localUrl;
      }

      this._uploaded = isBoolean(uploaded) ? uploaded : false;

      const mediumBaseUrl = getMediaUrlByMedia(medium);
      if (isNotNil(mediumBaseUrl)) {
        if (isNotNil(mediumTemplateId)) {
          this._URL = appendQueryParams(mediumBaseUrl, {
            templateId: mediumTemplateId,
          });
        } else if (fromTemplate && isNotNil(templateId)) {
          this._URL = appendQueryParams(mediumBaseUrl, { templateId });
        } else {
          this._URL = mediumBaseUrl;
        }
      }
    }
  }

  /*
    Convenience Methods
   */

  equals(inMedium) {
    if (!Medium.isMedium(inMedium)) return false;

    const propertyEquality =
      this.audioServerId === inMedium.audioServerId &&
      this.audioStreamDuration === inMedium.audioStreamDuration &&
      this.audioStreamStartTime === inMedium.audioStreamStartTime &&
      this.centerX === inMedium.centerX &&
      this.createdAt === inMedium.createdAt &&
      this.createdBy === inMedium.createdBy &&
      this.duration === inMedium.duration &&
      this.fontAdjustment === inMedium.fontAdjustment &&
      this.frameFormat === inMedium.frameFormat &&
      this.fromTemplate === inMedium.fromTemplate &&
      this.height === inMedium.height &&
      this.mediaId === inMedium.mediaId &&
      this.loaded === inMedium.loaded &&
      this.fileName === inMedium.fileName &&
      this.numFrames === inMedium.numFrames &&
      this.processedAudio === inMedium.processedAudio &&
      deepEqual(this.processors, inMedium.processors) &&
      this.serverId === inMedium.serverId &&
      this.source === inMedium.source &&
      this.svg === inMedium.svg &&
      this.templateId === inMedium.templateId &&
      this.mediaType === inMedium.mediaType &&
      this.mimeType === inMedium.mimeType &&
      this.organizationId === inMedium.organizationId &&
      this.publisherId === inMedium.publisherId &&
      this.updatedAt === inMedium.updatedAt &&
      this.uploadComplete === inMedium.uploadComplete &&
      this.uploaded === inMedium.uploaded &&
      // this.uploadedPercent === inMedium.uploadedPercent &&
      this.uploadFailed === inMedium.uploadFailed &&
      this.URL === inMedium.URL &&
      this.usePngSequence === inMedium.usePngSequence &&
      this.width === inMedium.width &&
      deepEqual(this.variants, inMedium.variants);

    return propertyEquality;
  }

  /**
   * Set values related to a medium being loaded on client
   * @param {Boolean} success
   */
  setLoaded(success = true) {
    this._loaded = success;
  }

  /**
   * Set values related to a medium being uploaded
   * @param {Boolean} success
   */
  setUploaded(success = true) {
    this._uploadedPercent = success ? 1 : 0;
    this._uploadComplete = success;
    this._uploaded = success;
    this._uploadFailed = !success;
  }

  /*
    Getters and Setters for Medium properties
   */

  get audioServerId() {
    return this._audioServerId;
  }

  set audioServerId(value) {
    this._audioServerId = value;
  }

  get audioStreamDuration() {
    return this._audioStreamDuration;
  }

  set audioStreamDuration(value) {
    this._audioStreamDuration = value;
  }

  get audioStreamStartTime() {
    return this._audioStreamStartTime;
  }

  set audioStreamStartTime(value) {
    this._audioStreamStartTime = value;
  }

  get centerX() {
    return this._centerX;
  }

  set centerX(value) {
    this._centerX = value;
  }

  get createdAt() {
    return this._createdAt;
  }

  get createdBy() {
    return this._createdBy;
  }

  get duration() {
    return this._duration;
  }

  set duration(duration) {
    this._duration = duration;
  }

  get fileName() {
    return this._fileName;
  }

  get fontAdjustment() {
    return this._fontAdjustment || 0;
  }

  get frameFormat() {
    return this._frameFormat;
  }

  get fromTemplate() {
    return this._fromTemplate;
  }

  get height() {
    return this._height;
  }

  set height(value) {
    this._height = value;
  }

  get isReadyForEdit() {
    return this.loaded && this.uploaded;
  }

  get loaded() {
    return this._loaded;
  }

  set loaded(value) {
    this._loaded = Boolean(value);
  }

  get localUrl() {
    return this._localUrl;
  }

  set localUrl(value) {
    this._localUrl = value;
  }

  /** @returns {string} */
  get mediaId() {
    return this._mediaId;
  }

  set mediaId(value) {
    this._mediaId = value;
  }

  /** @returns {string} */
  get mediaType() {
    return this._mediaType;
  }

  get mimeType() {
    return this._mimeType;
  }

  get numFrames() {
    return this._numFrames;
  }

  get organizationId() {
    return this._organizationId;
  }

  set organizationId(value) {
    this._organizationId = value;
  }

  get processedAudio() {
    return this._processedAudio;
  }

  set processedAudio(value) {
    this._processedAudio = value;
  }

  get processors() {
    return this._processors;
  }

  set processors(value) {
    this._processors = value;
  }

  get publisherId() {
    return this._publisherId;
  }

  set publisherId(value) {
    this._publisherId = value;
  }

  get serverId() {
    return this._serverId;
  }

  set serverId(value) {
    this._serverId = value;
  }

  get source() {
    return this._source;
  }

  get svg() {
    return this._svg;
  }

  get templateId() {
    return this._templateId;
  }

  get updatedAt() {
    return this._updatedAt;
  }

  get uploadComplete() {
    return this._uploadComplete;
  }

  set uploadComplete(value) {
    this._uploadComplete = value;
  }

  get uploaded() {
    return this._uploaded;
  }

  get uploadedPercent() {
    return this._uploadedPercent;
  }

  get uploadFailed() {
    return this._uploadFailed;
  }

  set uploadFailed(value) {
    this._uploadFailed = value;
  }

  get URL() {
    return this._URL;
  }

  set URL(value) {
    this._URL = value;
  }

  get usePngSequence() {
    return this._usePngSequence;
  }

  get variants() {
    return this._variants;
  }

  get width() {
    return this._width;
  }

  set width(value) {
    this._width = value;
  }

  getFrameUrls(frameRange) {
    if (isNil(frameRange) || isNil(this._serverId)) return undefined;

    const frameUrls = new Map();
    for (let i = frameRange.start; i < frameRange.end; i++) {
      const thumbnailOptions = { frameNumber: i };
      if (this._templateId) {
        thumbnailOptions.templateId = this._templateId;
      }
      frameUrls.set(i, getThumbnailUrl(this._serverId, thumbnailOptions));
    }
    return frameUrls;
  }

  /*
    Static Methods
   */

  /**
   * Checks if object is an instance of Medium
   *
   * @param inputObject
   * @returns {boolean}
   */
  static isMedium(inputObject) {
    return (
      inputObject instanceof Medium ||
      (inputObject && inputObject.prototype instanceof Medium)
    );
  }

  /**
   * @param {Medium} medium
   * @returns {CreativeDocumentMedium}
   */
  static getCreativeDocumentMedium(medium) {
    if (!Medium.isMedium(medium)) {
      throw new Error('Cannot get CreativeDocumentMedium');
    }

    const creativeDocumentMedium = new CreativeDocumentMedium(
      medium.mediaType,
      medium.mediaId,
    );

    // TODO - audit other properties
    creativeDocumentMedium.audioServerId = medium.audioServerId;
    creativeDocumentMedium.audioStreamDuration = medium.audioStreamDuration;
    creativeDocumentMedium.audioStreamStartTime = medium.audioStreamStartTime;
    creativeDocumentMedium.centerX = medium.centerX;
    creativeDocumentMedium.duration = medium.duration;
    creativeDocumentMedium.fileName = medium.fileName;
    creativeDocumentMedium.frameFormat = medium.frameFormat;
    creativeDocumentMedium.fromTemplate = medium.fromTemplate;
    creativeDocumentMedium.fontAdjustment = medium.fontAdjustment;
    creativeDocumentMedium.height = medium.height;
    creativeDocumentMedium.loaded = medium.loaded;
    creativeDocumentMedium.localUrl = medium.localUrl;
    creativeDocumentMedium.mimeType = medium.mimeType;
    creativeDocumentMedium.processedAudio = medium.processedAudio;
    creativeDocumentMedium.processors = medium.processors;
    creativeDocumentMedium.serverId = medium.serverId;
    creativeDocumentMedium.source = medium.source;
    creativeDocumentMedium.svg = medium.svg;
    creativeDocumentMedium.templateId = medium.templateId;
    creativeDocumentMedium.uploadComplete = medium.uploadComplete;
    creativeDocumentMedium.uploaded = medium.uploaded;
    creativeDocumentMedium.uploadedPercent = medium.uploadedPercent;
    creativeDocumentMedium.uploadFailed = medium.uploadFailed;
    creativeDocumentMedium.usePngSequence = medium.usePngSequence;
    creativeDocumentMedium.variants = medium.variants;
    creativeDocumentMedium.width = medium.width;

    // Special case URL
    creativeDocumentMedium.url = isNotEmptyString(medium.serverId)
      ? `/api/v1/media/${medium.serverId}/file`
      : medium.URL || '';

    return creativeDocumentMedium;
  }

  /**
   * Creates a new Medium from a wire object
   * @param {Object} inputWireObject
   * @return {Medium}
   */
  static fromWireObject(inputWireObject) {
    if (isNil(inputWireObject)) return inputWireObject;

    const {
      audio_media_id: audioServerId,
      audio_stream_duration: audioStreamDuration,
      audio_stream_start_time: audioStreamStartTime,
      center_x: centerX,
      created_at: createdAt,
      created_by: createdBy,
      file_name: fileName,
      frame_format: frameFormat,
      height,
      id: serverId,
      media_type: mimeType,
      number_of_frames: numFrames,
      organization_id: organizationId,
      processors,
      publisher_id: publisherId,
      snap_source: source,
      updated_at: updatedAt,
      uploadComplete = true,
      use_png_sequence: usePngSequence,
      variants,
      width,
    } = inputWireObject;

    const mediaType = getMediaTypeForMimeType(mimeType);

    const mediaObject = {
      audioServerId,
      audioStreamDuration,
      audioStreamStartTime,
      centerX,
      createdAt,
      createdBy,
      fileName,
      frameFormat,
      height,
      mediaType,
      mimeType,
      numFrames,
      organizationId,
      processedAudio: isNotNil(audioServerId), // Used for managing audio playback when server is still loading audio track (videos)
      processors,
      publisherId,
      serverId,
      source,
      updatedAt,
      uploadComplete,
      usePngSequence,
      variants,
      width,
    };

    // TODO(lemieux) Update to pass these in to constructor or use setters
    return new Medium(mediaObject);
  }

  /**
   * Creates a new Medium from CreativeDocumentMedium
   * @param {CreativeDocumentMedium} creativeDocumentMedium
   * @returns {Medium}
   */
  static fromCreativeDocumentObject(creativeDocumentMedium) {
    // creativeDocumentMedium is undefined for Group Layers
    if (isNil(creativeDocumentMedium)) return undefined;

    // TODO(lemieux) refactor constructor
    return new Medium(creativeDocumentMedium);
  }

  /**
   * Creates an object from Medium model object
   * @param {Medium} medium
   * @return {object}
   */
  static toJSON(medium) {
    if (!Medium.isMedium(medium)) {
      return undefined;
    }

    return Medium.getCreativeDocumentMedium(medium).toJSON();
  }
}
